import React, { useState } from 'react';
import { FileText, X, AlertCircle } from 'lucide-react';
import { renderOptions, todayLocalDate, describeSaveError, logError } from './helpers';

function ConvertWizard({ lead, onClose, onConvert, dropdownOptions }) {
  const [formData, setFormData] = useState({
    contractType: 'Management Agreement',
    contractNumber: '',
    // BUG-08: the LOCAL calendar date. `new Date().toISOString()` is the UTC
    // date, so from ~18:00 Chicago onward this defaulted the contract's start
    // date to tomorrow.
    startDate: todayLocalDate(),
    endDate: '',
    value: lead.EstimatedValue || '',
    propertyAddress: lead.LinkedPropertyAddress || '',
    terms: '',
    notes: `Converted from lead.${lead.PropertyName ? ` Property: ${lead.PropertyName}.` : ''}${lead.PropertyInterest ? ` Original interest: ${lead.PropertyInterest}` : ''}`
  });

  // BUG-18 #26: the conversion's outcome, rendered in this modal. onConvert
  // (App's handleConvert) rejects with the axios error; describeSaveError turns
  // it into { message, details, hint, gone } off the RESPONSE only (SEC-25).
  const [saveError, setSaveError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Editing anything makes the previous failure stale — and brings Create
    // Contract back after a 404 withdrew it.
    setSaveError(null);
  };

  // BUG-18 #26: was fire-and-forget, so a rejected conversion left the wizard
  // open showing nothing while App's banner painted under this overlay. Awaited
  // now, and the failure is rendered below the form. `submitting` closes the
  // double-click window the await opens: the server claims the lead (BUG-12), so
  // a second submit would come back as a confusing 409.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSaveError(null);
    try {
      await onConvert(lead.LeadId, formData);
      // Success: App closes the wizard, so there is nothing to do here.
    } catch (err) {
      logError('Error converting lead:', err);
      // 'lead' rather than 'contract': a 404 from POST /leads/:id/convert means
      // the LEAD is gone (no contract exists yet to have been deleted), and
      // that is the sentence describeSaveError's 404 branch builds.
      setSaveError(describeSaveError(err, 'lead'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="crm-modal__overlay" onClick={onClose}>
      <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal__header">
          <div>
            <h2 className="crm-modal__title">Convert Lead to Contract</h2>
            <p className="crm-convert-wizard__subtitle">
              {lead.ContactFirstName || lead.FirstName} {lead.ContactLastName || lead.LastName}
              {/* BUG-18: this was a mojibake em dash — the three UTF-8 bytes of
                  U+2014 (E2 80 94) stored as three CP1252 characters (U+00E2,
                  U+20AC, U+201D), which rendered to users as three pieces of
                  punctuation instead of a dash. The literal sequence is
                  deliberately NOT reproduced in this comment, so the file no
                  longer contains those bytes anywhere. */}
              {(lead.ContactCompany || lead.CompanyName) && ` — ${lead.ContactCompany || lead.CompanyName}`}
            </p>
          </div>
          <button onClick={onClose} className="crm-btn crm-btn--secondary crm-btn--icon" data-tooltip="Close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="crm-modal__body">
            <div className="crm-form__row">
              <div className="crm-form__group crm-form__col">
                <label className="crm-form__label">Contract Type *</label>
                <select
                  name="contractType"
                  value={formData.contractType}
                  onChange={handleChange}
                  className="crm-form__select"
                  required
                >
                  {renderOptions(dropdownOptions.ContractType)}
                </select>
              </div>
              <div className="crm-form__group crm-form__col">
                <label className="crm-form__label">Contract Number *</label>
                <input
                  type="text"
                  name="contractNumber"
                  value={formData.contractNumber}
                  onChange={handleChange}
                  className="crm-form__input"
                  required
                  placeholder="e.g. MGA-2024-005"
                />
              </div>
            </div>

            <div className="crm-form__row">
              <div className="crm-form__group crm-form__col">
                <label className="crm-form__label">Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className="crm-form__input"
                />
              </div>
              <div className="crm-form__group crm-form__col">
                <label className="crm-form__label">End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  className="crm-form__input"
                />
              </div>
            </div>

            <div className="crm-form__group">
              <label className="crm-form__label">Value ($)</label>
              <input
                type="number"
                name="value"
                value={formData.value}
                onChange={handleChange}
                className="crm-form__input"
                min="0"
                step="1000"
              />
            </div>

            <div className="crm-form__group">
              <label className="crm-form__label">Property Address</label>
              <input
                type="text"
                name="propertyAddress"
                value={formData.propertyAddress}
                onChange={handleChange}
                className="crm-form__input"
              />
            </div>

            <div className="crm-form__group">
              <label className="crm-form__label">Terms</label>
              <textarea
                name="terms"
                value={formData.terms}
                onChange={handleChange}
                className="crm-form__textarea"
                rows={3}
              />
            </div>

            <div className="crm-form__group">
              <label className="crm-form__label">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="crm-form__textarea"
                rows={3}
              />
            </div>
          </div>

          {/* BUG-18 #26: the failure, in the app's own error style, immediately
              above the button that produced it — outside the scrolling
              .crm-modal__body, so it cannot scroll out of view. Same placement
              and `.crm-error` convention FormModal uses. The form above is
              untouched, so the user's input is still on screen and editable. */}
          {saveError && (
            <div className="crm-error" style={{ margin: '0 1.5rem 1rem' }}>
              <AlertCircle size={18} className="crm-error__icon" />
              {saveError.message}
              {saveError.details.length > 0 && (
                <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.5rem' }}>
                  {saveError.details.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              )}
              {saveError.hint && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.813rem' }}>{saveError.hint}</div>
              )}
            </div>
          )}

          <div className="crm-modal__footer">
            <button type="button" onClick={onClose} className="crm-btn crm-btn--secondary">
              {saveError?.gone ? 'Close' : 'Cancel'}
            </button>
            {/* A deleted lead cannot be converted by trying harder, so the 404
                case drops Create Contract entirely. Editing any field clears
                saveError and brings it straight back. */}
            {!saveError?.gone && (
              <button type="submit" className="crm-btn crm-btn--success" disabled={submitting}>
                <FileText size={16} />
                {submitting ? 'Creating…' : 'Create Contract'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// Utility function

export default ConvertWizard;
